import * as z from 'zod';

import type {ProductFilters} from '@/modules/commerce/filters/query';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import {DOCUMENT_TYPE_FONT} from '../../font/constants';
import type {DynamicTextOverlay, ImageOverlay, MaskedColorOverlay} from '../../ProductImageStack';
import {getOverlayPlacement} from '../../ProductImageStack';
import type {StickerProductFragment} from '../query';
import {
	OBJECT_TYPE_STICKER_MULTICOLOR_PRINT_ELEMENT,
	OBJECT_TYPE_STICKER_TEXT_ELEMENT,
} from '../schema/names';
import {OBJECT_TYPE_STICKER_PRINT_ELEMENT} from '../schema/names';

/*----------------------------------- KEYS & ENCODING -----------------------------------*/

export const STICKER_PRINT_KEY = 'printId';
export const STICKER_FONT_KEY = 'fontId';
export const STICKER_TEXT_KEY = 'text';

export const STICKER_ELEMENT_KEYS = [
	STICKER_PRINT_KEY,
	STICKER_FONT_KEY,
	STICKER_TEXT_KEY,
] as const;

export type StickerElementKey = (typeof STICKER_ELEMENT_KEYS)[number];

export type StickerElementOptionKey = `option[${string}][${StickerElementKey}]`;

export function getStickerOptionKey(elementId: string, key: StickerElementKey) {
	return `option[${elementId}][${key}]`;
}

function parseStickerOption(
	data: FormData | URLSearchParams,
	elementId: string,
	key: StickerElementKey,
) {
	return data.get(getStickerOptionKey(elementId, key))?.toString() ?? null;
}

export function getStickerType(fragment?: StickerProductFragment | null) {
	return fragment?.stickerType === 'multiColor' ? 'multiColor' : 'singleColor';
}

/*----------------------------------- CONFIGURATION -----------------------------------*/

export const stickerTextElementConfigSchema = z.object({
	id: z.string(),
	[STICKER_FONT_KEY]: z.string(),
	[STICKER_TEXT_KEY]: z.string().nullable(),
});

export const stickerPrintElementConfigSchema = z.object({
	id: z.string(),
	[STICKER_PRINT_KEY]: z.string(),
});

export const stickerElementConfigSchema = z.union([
	stickerTextElementConfigSchema,
	stickerPrintElementConfigSchema,
]);

export type StickerTextElementConfig = z.infer<typeof stickerTextElementConfigSchema>;
export type StickerPrintElementConfig = z.infer<typeof stickerPrintElementConfigSchema>;
export type StickerElementConfig = z.infer<typeof stickerElementConfigSchema>;

export function isStickerTextElementConfig(
	element?: StickerElementConfig | null,
): element is StickerTextElementConfig {
	return (
		typeof element === 'object' &&
		element !== null &&
		STICKER_TEXT_KEY in element &&
		STICKER_FONT_KEY in element
	);
}

export function isStickerPrintElementConfig(
	element?: StickerElementConfig | null,
): element is StickerPrintElementConfig {
	return typeof element === 'object' && element !== null && STICKER_PRINT_KEY in element;
}

export function hasStickerElementConfigs(
	elements?: StickerElementConfig[] | null,
): elements is StickerElementConfig[] {
	return elements !== null && elements !== undefined && elements?.length > 0;
}

/*----------------------------------- FRAGMENT -----------------------------------*/

export type StickerFragElement =
	| NonNullable<StickerProductFragment['elements']>[number]
	| NonNullable<StickerProductFragment['multicolorElements']>[number];

export type StickerFragTextElement = Extract<
	StickerFragElement,
	{_type: typeof OBJECT_TYPE_STICKER_TEXT_ELEMENT}
>;
export type StickerFragPrintElement = Extract<
	StickerFragElement,
	| {_type: typeof OBJECT_TYPE_STICKER_PRINT_ELEMENT}
	| {_type: typeof OBJECT_TYPE_STICKER_MULTICOLOR_PRINT_ELEMENT}
>;

export function isStickerFragTextElement(
	element?: StickerFragElement | null,
): element is StickerFragTextElement {
	return element?._type === OBJECT_TYPE_STICKER_TEXT_ELEMENT;
}

export function isStickerFragPrintElement(
	element?: StickerFragElement | null,
): element is StickerFragPrintElement {
	return (
		element?._type === OBJECT_TYPE_STICKER_PRINT_ELEMENT ||
		element?._type === OBJECT_TYPE_STICKER_MULTICOLOR_PRINT_ELEMENT
	);
}

/*----------------------------------- SHARED -----------------------------------*/

export function isValidStickerElementConfig(
	config: StickerElementConfig | null,
	fragment: StickerProductFragment,
) {
	const selectable = getSelectableStickerElements(fragment);

	if (!config || !selectable || selectable.length === 0) {
		return null;
	}

	const selected = getSelectedStickerElement(config, selectable);

	if (!selected || !selected.collection) {
		return null;
	}

	return selected;
}

export function getSelectableStickerElements(fragment?: StickerProductFragment | null) {
	if (getStickerType(fragment) === 'singleColor') {
		return fragment?.elements ?? [];
	}

	return fragment?.multicolorElements ?? [];
}

export function parseStickerElementsFromData(fragment: StickerProductFragment, data: FormData) {
	const selectable = getSelectableStickerElements(fragment);

	if (!selectable || selectable.length === 0) {
		return [];
	}

	const elementConfigs: StickerElementConfig[] = [];

	for (const fragElement of selectable) {
		const elementId = fragElement._key;
		let configElement: StickerElementConfig | null = null;

		if (isStickerFragTextElement(fragElement)) {
			const fontId = parseStickerOption(data, elementId, STICKER_FONT_KEY);
			const text = parseStickerOption(data, elementId, STICKER_TEXT_KEY);

			if (!fontId) {
				continue;
			}

			configElement = {
				id: elementId,
				fontId,
				text,
			};
		}

		if (isStickerFragPrintElement(fragElement)) {
			const printId = parseStickerOption(data, elementId, STICKER_PRINT_KEY);

			if (!printId) {
				continue;
			}

			configElement = {
				id: elementId,
				printId,
			};
		}

		if (configElement && isValidStickerElementConfig(configElement, fragment)) {
			elementConfigs.push(configElement);
		}
	}

	return elementConfigs;
}

export function getSelectedStickerElement(
	elementConfig: StickerElementConfig,
	fragElements: StickerFragElement[],
) {
	const fragElement = fragElements.find((e) => e._key == elementConfig.id);

	if (!fragElement) {
		return null;
	}

	if (isStickerFragPrintElement(fragElement) && isStickerPrintElementConfig(elementConfig)) {
		const {name, placement, collection} = fragElement;

		const selected = collection?.prints?.find(
			(choice) => choice._key === elementConfig.printId,
		);

		return {name, placement, collection, selected, defaults: undefined, icon: undefined};
	}

	if (isStickerFragTextElement(fragElement) && isStickerTextElementConfig(elementConfig)) {
		const {name, placement, defaultFont, defaultText, collection} = fragElement;

		const selected = collection?.fonts?.find((choice) => choice._id === elementConfig.fontId);

		return {
			name,
			placement,
			collection,
			selected,
			icon: fragElement.icon,
			origin: fragElement.origin,
			fontSize: fragElement.fontSize,
			lineHeight: fragElement.lineHeight,
			defaults: {
				font: defaultFont,
				text: defaultText,
			},
		};
	}

	return null;
}

function getDefaultTextElement(element: StickerFragTextElement): StickerTextElementConfig | null {
	const {_key: id, collection} = element;

	if (!collection) {
		return null;
	}

	const fontId = element.defaultFont?._id ?? collection.fonts?.at(0)?._id;
	const text = element.defaultText ?? 'Din tekst her.';

	if (!fontId) {
		return null;
	}

	return {
		id,
		fontId,
		text,
	};
}

function getDefaultPrintElement(
	element: StickerFragPrintElement,
	brandIds?: string[],
): StickerPrintElementConfig | null {
	const {_key: id, collection} = element;

	if (!collection) {
		return null;
	}

	const {prints} = collection;

	if (!prints || prints.length === 0) {
		return null;
	}

	let printId: string | null = null;

	if (brandIds && brandIds.length > 0) {
		for (const {_key, brands} of prints) {
			if (!brands) {
				continue;
			}

			const found = brands.find((brand) => brandIds.includes(brand._id)) !== undefined;

			if (found) {
				printId = _key;
			}
		}
	} else {
		printId = prints[0]._key;
	}

	if (!printId) {
		return null;
	}

	return {
		id,
		printId,
	};
}

export function getStickerElementsAppropriateForProductFilters(
	fragment: StickerProductFragment,
	filters: ProductFilters,
) {
	const configElements: StickerElementConfig[] = [];

	const brandIds = filters.brandIds;

	if (!brandIds || brandIds.length === 0) {
		return configElements;
	}

	const selectable = getSelectableStickerElements(fragment);

	if (selectable.length === 0) {
		return configElements;
	}

	for (const fragElement of selectable) {
		if (isStickerFragTextElement(fragElement)) {
			const configElement = getDefaultTextElement(fragElement);
			if (configElement) {
				configElements.push(configElement);
			}
		}
		if (isStickerFragPrintElement(fragElement)) {
			const configElement = getDefaultPrintElement(fragElement, brandIds);
			if (configElement) {
				configElements.push(configElement);
			}
		}
	}

	return configElements;
}

function getDefaultImageOverlayForStickerFragPrintElement(
	element: StickerFragPrintElement,
	colorHex: string,
) {
	const image = element.defaultImage ?? element.collection?.prints?.at(0)?.image;

	if (!image) {
		return null;
	}

	const sanityImage = imageFromSanityImage(image);

	if (!sanityImage) {
		return null;
	}

	if (element._type === OBJECT_TYPE_STICKER_MULTICOLOR_PRINT_ELEMENT) {
		return {
			_type: 'imageOverlay',
			image: sanityImage,
			placement: getOverlayPlacement(element.placement),
		} satisfies ImageOverlay;
	}

	return {
		_type: 'maskedColorOverlay',
		placement: getOverlayPlacement(element.placement),
		maskImage: sanityImage,
		colorHex,
	} satisfies MaskedColorOverlay;
}

function getDefaultImageOverlayForStickerFragTextElement(
	fragElement: StickerFragTextElement,
	colorHex: string,
) {
	const font = fragElement.defaultFont ?? fragElement.collection?.fonts?.at(0);

	if (!font) {
		return null;
	}

	const {file} = font;

	if (!file) {
		return null;
	}

	const fontId = font.name ?? file._id;
	const fontUrl = file.url;

	if (!fontUrl) {
		return null;
	}

	const icon = imageFromSanityImage(fragElement.icon);

	return {
		_type: 'dynamicTextOverlay',
		font: fontId,
		icon: icon ?? undefined,
		fontUrl,
		fontSize: fragElement.fontSize ?? 10,
		lineHeight: fragElement.lineHeight ?? 1.5,
		origin: fragElement.origin,
		colorHex,
		placement: getOverlayPlacement(fragElement.placement),
		text: fragElement?.defaultText ?? '',
	} satisfies DynamicTextOverlay;
}

export function getDefaultImageOverlayForStickerElement(
	element: StickerFragElement,
	colorHex: string,
) {
	if (isStickerFragTextElement(element)) {
		return getDefaultImageOverlayForStickerFragTextElement(element, colorHex);
	}
	if (isStickerFragPrintElement(element)) {
		return getDefaultImageOverlayForStickerFragPrintElement(element, colorHex);
	}
}

export function getImageOverlayForStickerElement(
	configElement: StickerElementConfig,
	fragElements: StickerFragElement[],
	colorHex: string,
) {
	const result = getSelectedStickerElement(configElement, fragElements);

	if (!result) {
		return null;
	}

	const {placement: p, selected, defaults} = result;

	const placement = getOverlayPlacement(p);

	if (isStickerTextElementConfig(configElement) && selected?._type === DOCUMENT_TYPE_FONT) {
		const {file = defaults?.font?.file} = selected;

		if (!file) {
			return null;
		}

		const font = selected.name ?? file._id;
		const fontUrl = file.url;

		if (!fontUrl) {
			return null;
		}

		const icon = imageFromSanityImage(result.icon);

		return {
			_type: 'dynamicTextOverlay',
			font,
			icon: icon ?? undefined,
			fontUrl,
			fontSize: result.fontSize ?? 10,
			lineHeight: result.lineHeight ?? 1.5,
			origin: result.origin,
			colorHex,
			placement,
			text: configElement.text ?? defaults?.text ?? '',
		} satisfies DynamicTextOverlay;
	}

	if (isStickerPrintElementConfig(configElement)) {
		if (selected?._type === 'singleColorPrint') {
			const sanityImage = imageFromSanityImage(selected?.image);

			if (!sanityImage) {
				return null;
			}

			return {
				_type: 'maskedColorOverlay',
				placement,
				maskImage: sanityImage,
				colorHex,
			} satisfies MaskedColorOverlay;
		} else if (selected?._type === 'multiColorPrint') {
			const sanityImage = imageFromSanityImage(selected?.image);

			if (!sanityImage) {
				return null;
			}

			return {
				_type: 'imageOverlay',
				placement,
				image: sanityImage,
			} satisfies ImageOverlay;
		}
	}
}

export function getDefaultStickerElements(
	fragment: StickerProductFragment,
): StickerElementConfig[] {
	const selectable = getSelectableStickerElements(fragment);
	const configElements: StickerElementConfig[] = [];

	if (selectable.length === 0) {
		return configElements;
	}

	for (const element of selectable) {
		if (isStickerFragTextElement(element)) {
			const configElement = getDefaultTextElement(element);
			if (configElement) {
				configElements.push(configElement);
			}
		}

		if (isStickerFragPrintElement(element)) {
			const configElement = getDefaultPrintElement(element);
			if (configElement) {
				configElements.push(configElement);
			}
		}
	}

	return configElements;
}
